import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectIsDepositBlocked } from '~/components/account/ducks';
import { authenticationSelector } from '~/components/authentication/ducks/selectors';
import { selectIsDesktop } from '~/components/brand/ducks/selectors';
import {
	dispose,
	initialize,
	selectInstantDepositInfo,
	selectIsInitialized,
	selectIsLoading
} from '~/components/payment/ducks/quick-deposit-duck';
import { QDType } from '~/components/payment/util/constants';
import { AppDispatch, RootState } from '~/store/types';

type QDProps = {
	isEnabled: boolean;
	shouldRefreshOnInit: boolean;
	type: QDType;
};

const useQuickDeposit = ({ isEnabled, shouldRefreshOnInit = true, type = QDType.casino }: QDProps) => {
	const dispatch: AppDispatch = useDispatch();

	const isAuthenticated = useSelector(authenticationSelector);
	const isDesktop = useSelector(selectIsDesktop);
	const isDepositBlocked = useSelector<RootState, boolean>(selectIsDepositBlocked);

	useEffect(() => {
		if (!isDepositBlocked && isEnabled && isAuthenticated && !isDesktop) {
			dispatch(initialize(shouldRefreshOnInit, type));
		}
	}, [isEnabled, isAuthenticated, isDesktop, shouldRefreshOnInit, dispatch, type, isDepositBlocked]);

	useEffect(() => {
		if (!isAuthenticated || isDesktop) {
			dispatch(dispose());
		}
	}, [isAuthenticated, isDesktop, dispatch]);

	const isInitialized = useSelector(selectIsInitialized);
	const isLoading = useSelector(selectIsLoading);
	const instantDepositInfo = useSelector(selectInstantDepositInfo);

	const isQuickDepositAvailable =
		!isDepositBlocked && isEnabled && isAuthenticated && !isDesktop && !!instantDepositInfo?.quickDepositAvailable;

	return useMemo(
		() => ({
			isInitialized,
			isLoading,
			isQuickDepositAvailable,
			instantDepositInfo
		}),
		[isInitialized, isLoading, isQuickDepositAvailable, instantDepositInfo]
	);
};

export default useQuickDeposit;
